package com.iptvplayer.web.ui

import com.iptvplayer.shared.util.Constants
import kotlinx.browser.window

/**
 * Watch Together client calls (web only). REST for create/join (via fetch) plus
 * helpers to build the WebSocket URL and the shareable deep link. The realtime
 * sync itself lives in watchtogether.js (window.IptvWatch).
 */
object WatchApi {

    /** Host creates a room for the currently-playing content. Requires the user's access token. */
    fun createRoom(
        streamUrl: String,
        contentId: String,
        name: String,
        type: String,
        logo: String?,
        onOk: (String) -> Unit,
        onErr: (String) -> Unit,
    ) {
        val content = js("({})")
        content.stream_url = streamUrl
        content.content_id = contentId
        content.name = name
        content.type = type
        content.logo = logo
        val body = js("({})")
        body.content = content
        httpJson("POST", "${Constants.BASE_URL}/api/watch", WebSession.token, kotlin.js.JSON.stringify(body),
            { data -> onOk(data.room_id as String) }, onErr)
    }

    /** Anyone with the link joins; returns content, current state, and a guest stream token. */
    fun joinRoom(roomId: String, onOk: (dynamic) -> Unit, onErr: (String) -> Unit) {
        httpJson("POST", "${Constants.BASE_URL}/api/watch/$roomId/join", null, null, onOk, onErr)
    }

    /** A display name for chat/voice: the logged-in username, else a random guest. */
    fun displayName(): String =
        WebSession.userName?.takeIf { it.isNotBlank() } ?: "Guest-${(1000..9999).random()}"

    /** Same-origin WS URL (works whether the page is http or https). */
    fun wsUrl(roomId: String): String {
        val loc = window.location
        val scheme = if (loc.protocol == "https:") "wss" else "ws"
        val token = WebSession.token ?: ""
        return "$scheme://${loc.host}/api/watch/$roomId/ws" +
            "?token=${encodeURIComponent(token)}&name=${encodeURIComponent(displayName())}"
    }

    fun shareLink(roomId: String): String = "${window.location.origin}/#/watch/$roomId"

    /** Fan-out stream URL: the server keeps ONE upstream to the provider per room. */
    fun relayUrl(roomId: String): String {
        val token = WebSession.token ?: ""
        return "${Constants.BASE_URL}/api/watch/$roomId/stream?token=${encodeURIComponent(token)}"
    }
}

private fun httpJson(
    method: String,
    url: String,
    token: String?,
    body: String?,
    onOk: (dynamic) -> Unit,
    onErr: (String) -> Unit,
) {
    val init = js("({})")
    init.method = method
    val headers = js("({})")
    headers["Content-Type"] = "application/json"
    if (token != null) headers["Authorization"] = "Bearer $token"
    init.headers = headers
    if (body != null) init.body = body
    window.asDynamic().fetch(url, init).then({ resp ->
        if (resp.ok as Boolean) {
            resp.json().then({ data -> onOk(data) })
        } else {
            onErr("HTTP ${resp.status}")
        }
        Unit
    }).catch({ e: dynamic -> onErr(e.toString()); Unit })
}
